import { useState } from "react";
import { toast } from "sonner";
import { BookHeart, ExternalLink, Loader2, RefreshCw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, inputCls } from "@/components/kit";
import { ShareButton } from "@/components/ShareDialog";
import { api, errMsg } from "@/lib/api";

function Editor({ b, setB }) {
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const setPage = (i, k, v) => setB({ ...b, pages: b.pages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nomi (sposi, festeggiato…)"><input className={inputCls} value={b.nomi || ""} onChange={set("nomi")} data-testid="brochure-names" /></Field>
        <Field label="Titolo di copertina"><input className={inputCls} value={b.titolo || ""} onChange={set("titolo")} data-testid="brochure-title" /></Field>
        <Field label="Sottotitolo" wide><input className={inputCls} value={b.sottotitolo || ""} onChange={set("sottotitolo")} data-testid="brochure-subtitle" /></Field>
      </div>
      {b.pages.map((p, i) => (
        <div key={i} className="rounded-lg border border-[#D4E6EC] p-3 space-y-2" data-testid={`brochure-page-edit-${i}`}>
          <input className={inputCls} value={p.titolo} onChange={(e) => setPage(i, "titolo", e.target.value)} data-testid={`brochure-page-title-${i}`} />
          <textarea rows={4} className={inputCls} value={p.testo} onChange={(e) => setPage(i, "testo", e.target.value)} data-testid={`brochure-page-text-${i}`} />
        </div>
      ))}
      <Field label="Frase finale"><input className={inputCls} value={b.chiusura || ""} onChange={set("chiusura")} data-testid="brochure-closing" /></Field>
    </div>
  );
}

export function BrochureButton({ source, id, phone, nome, flush }) {
  const [open, setOpen] = useState(false);
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  const url = b ? `${window.location.origin}/brochure/${b.token}` : "";
  const openDialog = async () => {
    try {
      setB((await api.post("/brochures", { source, id })).data);
    } catch {
      setB(null);
    }
  };
  const ensureExisting = async () => {
    try {
      await api.get("/brochures/by", { params: { source, id } });
      return true;
    } catch {
      return false;
    }
  };
  const openDialog2 = async () => {
    if (flush) await flush();
    setOpen(true);
    setB(null);
    if (await ensureExisting()) await openDialog();
  };
  const generate = async (regenerate, lang) => {
    setBusy(lang ? `lang-${lang}` : regenerate ? "regen" : "new");
    try {
      if (flush) await flush();
      if (regenerate && b) await api.put(`/brochures/${b.id}`, { nomi: b.nomi });
      setB((await api.post("/brochures", { source, id, regenerate, ...(lang ? { lang } : {}) })).data);
      toast.success("Brochure pronta: rileggila e inviala");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const save = async () => {
    try {
      setB((await api.put(`/brochures/${b.id}`, { nomi: b.nomi, titolo: b.titolo, sottotitolo: b.sottotitolo, chiusura: b.chiusura, pages: b.pages })).data);
      toast.success("Brochure salvata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <Button variant="outline" className="rounded-full border-[#34B3B0] text-[#0B6E8F]" onClick={openDialog2} data-testid={`brochure-button-${source}`}><BookHeart size={15} className="mr-2" /> Brochure sfogliabile</Button>
      <Modal open={open} onOpenChange={setOpen} title="Brochure personalizzata" testId="brochure-dialog">
        {!b ? (
          <div className="space-y-4">
            <p className="text-sm text-[#47606B]">L'IA scrive una brochure emozionale su misura per {nome || "il cliente"}: nomi, evento, data, sala, menù e foto della galleria. Il cliente la sfoglia online girando le pagine e può scaricare il PDF. Non contiene prezzi.</p>
            <Button className="rounded-full" onClick={() => generate(false)} disabled={!!busy} data-testid="brochure-generate">{busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sto scrivendo la brochure… (circa 30 secondi)</> : <><BookHeart size={14} className="mr-2" /> Crea la brochure</>}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(url, "_blank")} data-testid="brochure-preview"><ExternalLink size={13} className="mr-1" /> Sfoglia</Button>
              <ShareButton size="sm" testId="brochure-whatsapp" label="Invia su WhatsApp" link={url} linkLabel="📖 Sfogli la sua brochure:" phone={phone}
                body={`🌟 Gentile *${nome || "Cliente"}*,\n\n✨ abbiamo preparato una *brochure dedicata a ${b.nomi}*, da sfogliare pagina dopo pagina: la location sul mare, il vostro giorno, il menù e tante idee su misura. 🌊📖\n\n💬 Saremo felici di accogliervi per una degustazione o un sopralluogo!\n\nUn cordiale saluto 🌊`} />
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => generate(true)} disabled={!!busy} data-testid="brochure-regenerate">{busy === "regen" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <RefreshCw size={13} className="mr-1" />} Riscrivi con IA</Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => generate(true, b.lang === "en" ? "it" : "en")} disabled={!!busy} data-testid="brochure-lang">{busy.startsWith("lang") ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}{b.lang === "en" ? "🇮🇹 Riscrivi in italiano" : "🇬🇧 Versione in inglese"}</Button>
            </div>
            <div className="text-xs break-all text-[#7B929C]" data-testid="brochure-url">{url}</div>
            <Editor b={b} setB={setB} />
            <Button className="rounded-full" onClick={save} data-testid="brochure-save"><Save size={14} className="mr-2" /> Salva modifiche</Button>
          </div>
        )}
      </Modal>
    </>
  );
}
